/**
 * One cell of a per-stage status column (owner 2026-10-25) — the column of
 * stage S for company C, rendered per `stageStatusCellState`:
 *
 * - C is not in S (or has no stage): a muted `—`, read-only, no control.
 * - C is in S, read-only: the status name, or `—` when it holds none. When the
 *   page expects a write but cannot offer one (`disabledReason`, e.g. the
 *   master failed to load), a disabled select carries the reason on a Tooltip
 *   wrapper instead.
 * - C is in S, editable: a native `Select` offering "—" (no status) plus ONLY
 *   stage S's statuses — the active ones, and C's held one even if retired.
 *
 * The look mirrors CompaniesPage's `StageStatusCell` / `StageStatusValue`.
 *
 * ⚠️ Matched by ID only (`company.stage.id` vs `column.stageId`); names are
 * labels. The cell never PATCHes: `onChange` hands the pick to the page, which
 * runs `useStageStatusChange` (date / reason prompts, success verified on
 * `data.stageStatus?.id`). `saving` and `error` come back from that flow.
 */

import { Field, Select, Tooltip } from '@/components/ui';
import { cx } from '@/lib/cx';
import { stageStatusCellState, type StageStatusColumn } from '@/lib/stage-status-columns';
import type { Company, CompanyStageStatus, CompanyStageStatusRef } from '@/lib/types';

export interface StageStatusColumnCellProps {
  company: Company;
  column: StageStatusColumn;
  allStatuses: readonly CompanyStageStatus[];
  canEdit: boolean;
  /** Shown on a wrapper when `canEdit` is false but a write is expected. */
  disabledReason?: string | null;
  saving?: boolean;
  error?: string | null;
  /** The page runs the change flow. */
  onChange: (company: Company, stageStatusId: number | null) => void;
  variant?: 'table' | 'card';
}

function Dash() {
  return <span className="text-sm text-fg-subtle">—</span>;
}

function StatusValue({ status }: { status: CompanyStageStatusRef | null }) {
  if (!status) return <Dash />;
  return <span className="text-sm text-fg-secondary">{status.name}</span>;
}

function Spinner() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      aria-hidden="true"
      className="h-3.5 w-3.5 shrink-0 animate-spin text-fg-subtle motion-reduce:animate-none"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}

export function StageStatusColumnCell({
  company,
  column,
  allStatuses,
  canEdit,
  disabledReason,
  saving = false,
  error,
  onChange,
  variant = 'table',
}: StageStatusColumnCellProps) {
  const state = stageStatusCellState(company, column, allStatuses, canEdit);
  const label = `${column.label} for ${company.name}`;
  const errorText = error ? error : undefined;

  if (state.kind === 'other-stage') {
    return errorText ? (
      <Field label={label} hideLabel error={errorText}>
        {() => <Dash />}
      </Field>
    ) : (
      <Dash />
    );
  }

  const stored = state.status;
  const reason = disabledReason?.trim() ? disabledReason.trim() : null;

  if (!state.editable && !reason) {
    return errorText ? (
      <Field label={label} hideLabel error={errorText}>
        {() => <StatusValue status={stored} />}
      </Field>
    ) : (
      <StatusValue status={stored} />
    );
  }

  // The held status is always offered, even when the options do not carry it.
  const choices: { value: string; label: string }[] = state.editable
    ? state.options.map((status) => ({
        value: String(status.id),
        // "(retired)", the wording `StageStatusCell` uses for the same case.
        label: status.isActive ? status.name : `${status.name} (retired)`,
      }))
    : [];
  if (stored && !choices.some((choice) => choice.value === String(stored.id))) {
    choices.unshift({ value: String(stored.id), label: stored.name });
  }

  const locked = !state.editable;
  const disabled = locked || saving;

  return (
    <Field label={label} hideLabel error={errorText}>
      {({ id, 'aria-describedby': describedBy }) => {
        const select = (
          <Select
            id={id}
            aria-describedby={describedBy}
            aria-busy={saving || undefined}
            value={stored ? String(stored.id) : ''}
            invalid={Boolean(errorText)}
            disabled={disabled}
            // A disabled control takes no hover; let it reach the Tooltip wrapper.
            className={locked && reason ? 'pointer-events-none' : undefined}
            onChange={(event) => {
              const next = event.target.value;
              onChange(company, next === '' ? null : Number(next));
            }}
          >
            <option value="">—</option>
            {choices.map((choice) => (
              <option key={choice.value} value={choice.value}>
                {choice.label}
              </option>
            ))}
          </Select>
        );

        return (
          <div
            className={cx(
              'flex items-center gap-1.5',
              variant === 'table' ? 'min-w-[9rem]' : 'w-full min-w-0',
            )}
          >
            {locked && reason ? (
              <Tooltip label={reason} className="block min-w-0 flex-1">
                <div className="min-w-0">{select}</div>
              </Tooltip>
            ) : (
              <div className="min-w-0 flex-1">{select}</div>
            )}
            {saving && <Spinner />}
          </div>
        );
      }}
    </Field>
  );
}
